import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiPackage,
  FiSearch,
  FiPlus,
  FiRefreshCcw,
  FiFilter,
  FiBook,
  FiLayers,
  FiActivity,
} from "react-icons/fi";
import Button from "../../components/Button/Button";
import adminApi from "../../api/adminApi";
import Table from "../../components/Table/Table";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Select from "../../components/Select/Select";
import Modal from "../../components/Modal/Modal";
import FormInput from "../../components/FormInput/FormInput";
import "./Modules.css";

const Modules = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modules, setModules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkModulesData, setBulkModulesData] = useState([
    { title: "", description: "", order: 1, duration: "", isOpen: false },
  ]);
  const [bulkBatchId, setBulkBatchId] = useState("");

  useEffect(() => {
    loadCourses();
    loadBatches();
  }, []);

  // Load modules when batches are loaded
  useEffect(() => {
    if (batches.length > 0) {
      loadModules();
    }
  }, [batches.length]);

  // Reload modules when filters or page change
  useEffect(() => {
    if (batches.length > 0) {
      loadModules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, courseFilter, batchFilter, statusFilter]);

  const loadCourses = async () => {
    try {
      const response = await adminApi.get("/courses", {
        params: { limit: 1000 },
      });
      const coursesList = response.data?.courses || response.data?.data || [];
      setCourses(coursesList);
    } catch (err) {
      console.error("Failed to load courses:", err);
    }
  };

  const loadBatches = async () => {
    try {
      const response = await adminApi.get("/batches", {
        params: { limit: 1000 },
      });
      const batchesList = response.data?.batches || response.data?.data || [];
      setBatches(batchesList);
    } catch (err) {
      console.error("Failed to load batches:", err);
    }
  };

  const loadModules = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch modules from all batches
      const allModules = [];
      const batchesToFetch =
        batchFilter !== "all"
          ? batches.filter((b) => b._id === batchFilter)
          : courseFilter !== "all"
          ? batches.filter((b) => (b.course?._id || b.course) === courseFilter)
          : batches;

      for (const batch of batchesToFetch) {
        try {
          const response = await adminApi.get(
            `/batches/${batch._id}/modules`
          );
          const modulesList =
            response.data?.modules ||
            response.data?.data ||
            response.data ||
            [];
          // Enrich modules with batch and course info
          const enrichedModules = modulesList.map((module) => ({
            ...module,
            batch: batch,
            batchName: batch.batchName,
            course: batch.course,
            courseTitle:
              batch.course &&
              typeof batch.course === "object" &&
              batch.course.title
                ? batch.course.title
                : "Unknown Course",
          }));
          allModules.push(...enrichedModules);
        } catch (err) {
          console.error(`Failed to load modules for batch ${batch._id}:`, err);
        }
      }

      // Apply status filter
      let filteredModules = allModules;
      if (statusFilter === "open") {
        filteredModules = allModules.filter((m) => m.isOpen);
      } else if (statusFilter === "closed") {
        filteredModules = allModules.filter((m) => !m.isOpen);
      }

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredModules = filteredModules.filter(
          (module) =>
            module.title?.toLowerCase().includes(query) ||
            module.description?.toLowerCase().includes(query) ||
            module.batchName?.toLowerCase().includes(query) ||
            module.courseTitle?.toLowerCase().includes(query)
        );
      }

      // Sort by course, then batch, then order
      filteredModules.sort((a, b) => {
        const courseCompare = (a.courseTitle || "").localeCompare(
          b.courseTitle || ""
        );
        if (courseCompare !== 0) return courseCompare;
        const batchCompare = (a.batchName || "").localeCompare(
          b.batchName || ""
        );
        if (batchCompare !== 0) return batchCompare;
        return (a.order || 0) - (b.order || 0);
      });

      setModules(filteredModules);
      setTotalPages(Math.ceil(filteredModules.length / 25));
    } catch (err) {
      console.error("Failed to load modules:", err);
      setError(err.response?.data?.message || "Failed to load modules");
    } finally {
      setLoading(false);
    }
  };

  const filteredModules = useMemo(() => {
    const startIndex = (page - 1) * 25;
    return modules.slice(startIndex, startIndex + 25);
  }, [modules, page]);

  const columns = [
    {
      key: "order",
      label: "Order",
      render: (row) => <div className="order-badge">{row.order || "—"}</div>,
    },
    {
      key: "title",
      label: "Module Title",
      render: (row) => (
        <Link to={`/modules/${row._id}`} className="module-link">
          <strong>{row.title || "Untitled"}</strong>
        </Link>
      ),
    },
    {
      key: "course",
      label: "Course",
      render: (row) => (
        <Link
          to={`/courses/${
            typeof row.course === "object" ? row.course._id : row.course
          }`}
          className="course-link"
        >
          {row.courseTitle || "Unknown Course"}
        </Link>
      ),
    },
    {
      key: "batch",
      label: "Batch",
      render: (row) => (
        <Link
          to={`/batches/${row.batch?._id || row.batch}`}
          className="batch-link"
        >
          {row.batchName || "Unknown Batch"}
        </Link>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      render: (row) => row.duration || "—",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const isOpen = row.isOpen;
        return (
          <div className="status-group">
            {isOpen && (
              <span className="open-indicator">
                <FiActivity size={12} />
                Open
              </span>
            )}
            <StatusBadge status={isOpen ? "active" : "closed"} />
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="action-buttons">
          <Button
            size="small"
            variant="secondary"
            onClick={() => navigate(`/modules/${row._id}`)}
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="modules-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Module Management</p>
          <h1>Modules</h1>
          <p className="subhead">Manage all course modules across batches</p>
        </div>
        <div className="header-actions">
          <Button variant="secondary" onClick={loadModules}>
            <FiRefreshCcw size={18} />
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowBulkModal(true)}
            disabled={batches.length === 0}
          >
            <FiPlus size={18} />
            Bulk Create
          </Button>
          <Button onClick={() => navigate("/modules/new")}>
            <FiPlus size={18} />
            Create Module
          </Button>
        </div>
      </header>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <div className="filters-section">
        <div className="search-bar">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search modules by title, description, course, or batch..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="filters-row">
          <Select
            label="Course"
            value={courseFilter}
            onChange={(e) => {
              setCourseFilter(e.target.value);
              setBatchFilter("all");
              setPage(1);
            }}
            options={[
              { value: "all", label: "All Courses" },
              ...courses.map((c) => ({ value: c._id, label: c.title })),
            ]}
          />
          <Select
            label="Batch"
            value={batchFilter}
            onChange={(e) => {
              setBatchFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: "all", label: "All Batches" },
              ...(courseFilter !== "all"
                ? batches
                    .filter((b) => (b.course?._id || b.course) === courseFilter)
                    .map((b) => ({ value: b._id, label: b.batchName }))
                : batches.map((b) => ({ value: b._id, label: b.batchName }))),
            ]}
          />
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: "all", label: "All Status" },
              { value: "open", label: "Open" },
              { value: "closed", label: "Closed" },
            ]}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading modules...</div>
      ) : filteredModules.length > 0 ? (
        <>
          <Table columns={columns} data={filteredModules} />
          {totalPages > 1 && (
            <div className="pagination">
              <Button
                variant="secondary"
                size="small"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="small"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <FiPackage size={48} />
          <h3>No modules found</h3>
          <p>
            {searchQuery ||
            courseFilter !== "all" ||
            batchFilter !== "all" ||
            statusFilter !== "all"
              ? "Try adjusting your filters"
              : "Create your first module to get started"}
          </p>
          {!searchQuery &&
            courseFilter === "all" &&
            batchFilter === "all" &&
            statusFilter === "all" && (
              <Button onClick={() => navigate("/modules/new")}>
                <FiPlus size={18} />
                Create Module
              </Button>
            )}
        </div>
      )}

      {/* Bulk Module Creation Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => {
          setShowBulkModal(false);
          setBulkModulesData([
            {
              title: "",
              description: "",
              order: 1,
              duration: "",
              isOpen: false,
            },
          ]);
          setBulkBatchId("");
        }}
        title="Bulk Create Modules"
        size="large"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!bulkBatchId) {
              setError("Please select a batch");
              return;
            }

            const validModules = bulkModulesData.filter(
              (m) => m.title.trim() && m.order
            );
            if (validModules.length === 0) {
              setError("Please add at least one module");
              return;
            }

            setBulkLoading(true);
            setError(null);
            try {
              await adminApi.post(
                `/batches/${bulkBatchId}/modules/bulk`,
                validModules
              );
              setShowBulkModal(false);
              setBulkModulesData([
                {
                  title: "",
                  description: "",
                  order: 1,
                  duration: "",
                  isOpen: false,
                },
              ]);
              setBulkBatchId("");
              loadModules();
            } catch (err) {
              setError(
                err.response?.data?.message || "Failed to create modules"
              );
            } finally {
              setBulkLoading(false);
            }
          }}
        >
          <div className="bulk-modules-form">
            <div className="bulk-modules-header">
              <p>Select a batch and add multiple modules at once.</p>
              <Select
                label="Batch *"
                value={bulkBatchId}
                onChange={(e) => {
                  setBulkBatchId(e.target.value);
                  // Reset modules data when batch changes
                  setBulkModulesData([
                    {
                      title: "",
                      description: "",
                      order: 1,
                      duration: "",
                      isOpen: false,
                    },
                  ]);
                }}
                options={[
                  { value: "", label: "Select a batch" },
                  ...batches.map((b) => ({
                    value: b._id,
                    label: `${b.batchName} (${
                      b.course && typeof b.course === "object" && b.course.title
                        ? b.course.title
                        : "Course"
                    })`,
                  })),
                ]}
                required
              />
            </div>
            {bulkBatchId && (
              <div className="bulk-modules-list">
                {bulkModulesData.map((module, index) => (
                  <div key={index} className="bulk-module-item">
                    <div className="bulk-module-header">
                      <h4>Module {index + 1}</h4>
                      {bulkModulesData.length > 1 && (
                        <Button
                          type="button"
                          variant="danger"
                          size="small"
                          onClick={() => {
                            const updated = bulkModulesData.filter(
                              (_, i) => i !== index
                            );
                            updated.forEach((m, i) => {
                              m.order = i + 1;
                            });
                            setBulkModulesData(updated);
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <div className="bulk-module-fields">
                      <FormInput
                        label="Title *"
                        name={`title-${index}`}
                        value={module.title}
                        onChange={(e) => {
                          const updated = [...bulkModulesData];
                          updated[index].title = e.target.value;
                          setBulkModulesData(updated);
                        }}
                        required
                      />
                      <FormInput
                        label="Description"
                        name={`description-${index}`}
                        type="textarea"
                        rows={2}
                        value={module.description}
                        onChange={(e) => {
                          const updated = [...bulkModulesData];
                          updated[index].description = e.target.value;
                          setBulkModulesData(updated);
                        }}
                      />
                      <div className="bulk-module-row">
                        <FormInput
                          label="Order *"
                          name={`order-${index}`}
                          type="number"
                          value={module.order}
                          onChange={(e) => {
                            const updated = [...bulkModulesData];
                            updated[index].order =
                              parseInt(e.target.value) || 1;
                            setBulkModulesData(updated);
                          }}
                          required
                          min="1"
                        />
                        <FormInput
                          label="Duration"
                          name={`duration-${index}`}
                          value={module.duration}
                          onChange={(e) => {
                            const updated = [...bulkModulesData];
                            updated[index].duration = e.target.value;
                            setBulkModulesData(updated);
                          }}
                          placeholder="e.g., 2 weeks"
                        />
                      </div>
                      <div className="form-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={module.isOpen}
                            onChange={(e) => {
                              const updated = [...bulkModulesData];
                              updated[index].isOpen = e.target.checked;
                              setBulkModulesData(updated);
                            }}
                          />
                          <span>Module is open for students</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    const nextOrder = bulkModulesData.length + 1;
                    setBulkModulesData([
                      ...bulkModulesData,
                      {
                        title: "",
                        description: "",
                        order: nextOrder,
                        duration: "",
                        isOpen: false,
                      },
                    ]);
                  }}
                >
                  <FiPlus size={16} />
                  Add Another Module
                </Button>
              </div>
            )}
          </div>
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowBulkModal(false);
                setBulkModulesData([
                  {
                    title: "",
                    description: "",
                    order: 1,
                    duration: "",
                    isOpen: false,
                  },
                ]);
                setBulkBatchId("");
              }}
            >
              Cancel
            </Button>
            <Button type="submit" loading={bulkLoading} disabled={!bulkBatchId}>
              Create {bulkModulesData.filter((m) => m.title.trim()).length}{" "}
              Module(s)
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Modules;
